import '@treasury/omega/components/omega-badge.js';
import '@treasury/omega/components/omega-tooltip.js';
import '@treasury/omega/components/progress/omega-progress.js';
import dateFormatter from '@treasury/policy/lib/formatters/date.formatter.js';
import { css, html, LitElement, nothing } from 'lit';

class ChannelStatusBadge extends LitElement {
    static get properties() {
        return {
            fetchApproversFunction: Function,
            status: String,
            loading: Boolean,
            approvers: Object,
            approvalsNeeded: Number,
            approvalsCount: Number,
            type: String,
        };
    }

    constructor() {
        super();
        this.status = 'Approved';
        this.fetchApproversFunction = () => ({});
        this.loading = false;
        this.approvers = {};
        this.approvalsNeeded = 0;
        this.approvalsCount = 0;
        this.type = 'badge';
        this.fetchComplete = false;
    }

    hasStatus(status) {
        return this.status === status;
    }

    pendingApprovalStatus() {
        return this.hasStatus('Pending Approval') || this.hasStatus('PendingApproval');
    }

    generateApprovals() {
        const approvals = Array(this.approvalsNeeded).fill(null);
        const completedApprovals = this.approvalsCount;
        if (completedApprovals > 0) {
            approvals.splice(0, completedApprovals, true);
        }
        return approvals;
    }

    async getApprovers() {
        if (this.loading) return;
        this.loading = true;
        this.approvers = await this.fetchApproversFunction();
        // This is a stopgap as we migrate to the new auto-generated Users client where it returns the data wrapped in a response object instead of returning the data directly.
        // Some consumers of channel-status-badge still use the static UsersService.fetchApprovers method which returns the data directly.
        if (this.approvers.data) {
            this.approvers = this.approvers.data;
        }
        this.loading = false;
        this.fetchComplete = true;
    }

    renderApprover(approvers) {
        if (this.loading) return html`<omega-progress></omega-progress>`;
        const approverList = approvers?.completedApprovals?.map(
            approver =>
                html`<li>${approver.name}</li>
                    <li class="approver-item">${dateFormatter(approver.dateApproved)}</li>`
        );
        return html` <b>Approved By:</b>
            <ul>
                ${approverList}
            </ul>`;
    }

    renderAvailableApprovers(approvers) {
        if (this.loading) return html`<omega-progress></omega-progress>`;

        const numberOfApprovers = approvers?.availableApprovers?.length ?? 0;
        const numberOfColumns = 4;
        const approversColumnsCount = Math.ceil(numberOfApprovers / 4);
        const approversList = approvers?.availableApprovers?.map((approver, index) => {
            const paddingVal = index + numberOfColumns + 1 >= numberOfApprovers ? 0 : 16;
            return html`<li class="approver-name" style="padding-right: ${paddingVal}px;">
                ${approver}
            </li>`;
        });

        return html` <b>Eligible Approver(s):</b>
            <ul class="approver-name-list" style="min-width: ${116 * approversColumnsCount}px;">
                ${approversList}
            </ul>`;
    }

    renderApprovalIndicators() {
        const approvals = this.generateApprovals();
        const renderedApprovalList = approvals.map(approval => {
            const indicatorClass = approval ? 'active' : '';
            const renderedApprovedState = approval
                ? this.renderApprover(this.approvers)
                : this.renderAvailableApprovers(this.approvers);
            return html`
                <omega-tooltip
                    direction="left-top"
                    light
                    style="display: inline-block;"
                    @mouseover=${e => this.getApprovers(e)}
                >
                    <div class="approval-indicator ${indicatorClass}"></div>
                    <div slot="content" style="min-height: 60px;">${renderedApprovedState}</div>
                </omega-tooltip>
            `;
        });
        return html`<div class="approval-indicator-wrapper">${renderedApprovalList}</div>`;
    }

    renderActiveOrInitiatedBadge() {
        if (!this.hasStatus('Active') && !this.hasStatus('Initiated')) return nothing;
        return html`<omega-badge type="success">${this.status}</omega-badge>`;
    }

    renderFailedBadge() {
        if (!this.hasStatus('Failed')) return nothing;
        return html`<omega-badge type="error">${this.status}</omega-badge>`;
    }

    renderInactiveBadge() {
        if (!this.hasStatus('Inactive')) return nothing;
        return html`<omega-badge type="inactive">${this.status}</omega-badge>`;
    }

    renderExpiredBadge() {
        if (!this.hasStatus('Expired')) return nothing;
        return html`<omega-badge type="inactive">${this.status}</omega-badge>`;
    }

    renderCancelledOrUnInitiatedBadge() {
        if (!this.hasStatus('Cancelled') && !this.hasStatus('Uninitiated')) return nothing;
        return html`<omega-badge type="error">${this.status}</omega-badge>`;
    }

    renderScheduledOrSubmittedBadge() {
        if (!this.hasStatus('Scheduled') && !this.hasStatus('Submitted')) return nothing;
        return html`<omega-badge type="success">${this.status}</omega-badge>`;
    }

    renderReadyBadge() {
        if (!this.hasStatus('Ready')) return nothing;
        return html`<omega-badge type="success">${this.status}</omega-badge>`;
    }

    renderPostedBadge() {
        if (!this.hasStatus('Posted')) return nothing;
        return html`<omega-badge type="success">${this.status}</omega-badge>`;
    }

    renderTransmittedBadge() {
        if (!this.hasStatus('Transmitted')) return nothing;
        return html`<omega-badge type="success">${this.status}</omega-badge>`;
    }

    renderDeletedBadge() {
        if (!this.hasStatus('Deleted')) return nothing;
        return html`<omega-badge type="error">${this.status}</omega-badge>`;
    }

    renderApprovalRejectedBadge() {
        if (
            !this.hasStatus('Approval Rejected') &&
            !this.hasStatus('ApprovalRejected') &&
            !this.hasStatus('Rejected')
        )
            return nothing;
        return html`<omega-badge type="error">Approval Rejected</omega-badge>`;
    }

    renderPendingApprovalBadge() {
        if (!this.pendingApprovalStatus()) return nothing;
        return html`<omega-badge type="info" }>Pending Approval</omega-badge>
            ${this.renderApprovalIndicators()}`;
    }

    renderPendingProcessBadge() {
        if (!this.hasStatus('Pending Process')) return nothing;
        return html`<omega-badge type="pending">
            <div class="pending-process-content-wrapper">
                <span
                    >Pending <br />
                    Process</span
                >
                <div class="tooltip-span">
                    <omega-tooltip iconInheritColor icon="warning" direction="top-left">
                        <div class="pending-process-content" slot="content">
                            ACH Payment is currently pending processing. Please contact your
                            Financial Institution for additional information
                        </div>
                    </omega-tooltip>
                </div>
            </div>
        </omega-badge>`;
    }

    renderTrackerNode(isApproved, idx) {
        const connectorHidden = idx <= 0 ? ' transparent' : '';
        const active = isApproved ? ' active' : '';
        const approversList = isApproved
            ? this.renderApprover(this.approvers)
            : this.renderAvailableApprovers(this.approvers);

        return html`<span
            class="tooltip-wrapper"
            @mouseenter=${() => {
                if (!this.fetchComplete) this.getApprovers();
            }}
        >
            <span class="connector ${connectorHidden}"></span>
            <omega-tooltip light direction="bottom-left">
                <span class="node${active}"> </span>
                <div slot="content" class="tooltip-content">${approversList}</div>
            </omega-tooltip>
        </span> `;
    }

    renderTracker() {
        if (this.pendingApprovalStatus()) {
            const nodes = [];
            let nodesCount = 0;

            while (nodesCount < this.approvalsNeeded) {
                const approvedStatus = nodesCount < this.approvalsCount;
                nodes.push(this.renderTrackerNode(approvedStatus, nodesCount));
                nodesCount += 1;
            }

            return html`
                <div>Pending Approval</div>
                <div class="approval-status">
                    <span class="segment"> ${nodes} </span>
                </div>
            `;
        }
        return html`<div>${this.status}</div>`;
    }

    render() {
        if (this.type === 'tracker') {
            return html`${this.renderTracker()}`;
        }
        return html`
            ${this.renderActiveOrInitiatedBadge()} ${this.renderFailedBadge()}
            ${this.renderInactiveBadge()} ${this.renderExpiredBadge()}
            ${this.renderPendingApprovalBadge()} ${this.renderCancelledOrUnInitiatedBadge()}
            ${this.renderScheduledOrSubmittedBadge()} ${this.renderReadyBadge()}
            ${this.renderApprovalRejectedBadge()} ${this.renderPendingProcessBadge()}
            ${this.renderPostedBadge()} ${this.renderTransmittedBadge()}
            ${this.renderDeletedBadge()}
        `;
    }

    static get styles() {
        return css`
            :host {
                display: block;
            }
            ul {
                list-style: none;
                padding-left: 0;
                margin-bottom: 0;
                margin-top: 5px;
            }
            .approver-item {
                margin-bottom: 8px;
            }
            .approver-item:last-of-type {
                padding-bottom: 0;
            }
            b {
                display: block;
                margin-top: 5px;
            }
            omega-tooltip:last-child .approval-indicator::after {
                display: none;
            }
            .approval-indicator-wrapper {
                display: flex;
                flex-direction: row;
                margin: 6px 0 -2px;
            }
            .approval-indicator {
                width: 14px;
                height: 14px;
                background-color: var(--omega-white);
                border-radius: 50%;
                cursor: pointer;
                border: 1px solid var(--omega-text-light, #8c989f);
            }
            .approval-indicator::after {
                content: '';
                width: 10px;
                height: 1px;
                background-color: var(--omega-text-light, #8c989f);
                display: block;
                position: relative;
                left: 100%;
                top: 46%;
            }
            .approval-indicator.active {
                background-color: var(--omega-success-lighten-100);
                border-color: var(--omega-success-darken-100);
            }

            .approval-status {
                align-self: flex-start;
                position: relative;
                left: -11px;
            }
            .approval-status .tooltip-wrapper {
                display: inline-block;
                width: 22px;
            }
            .approval-status .segment {
                font-size: 0 !important;
                white-space: nowrap;
                position: relative;
            }
            .approval-status .segment > * {
                font-size: 13px;
            }
            .approval-status .segment .node {
                background-color: #ffffff;
                border-radius: 8px;
                border: 1px solid #cccccc;
                display: inline-block;
                height: 16px;
                line-height: 16px;
                text-align: center;
                width: 16px;
                z-index: 0;
                position: relative;
                vertical-align: middle;
                left: -2px;
            }
            .approval-status .segment .node.active {
                background-color: #76c891;
                color: #ffffff;
                border: #b7b7b7;
            }
            .approval-status .segment .connector {
                border-top: 1px solid #cccccc;
                display: inline-block;
                height: 1px;
                width: 6px;
                z-index: 0;
                position: relative;
                vertical-align: middle;
                position: relative;
                left: 6px;
            }
            .approval-status .segment .connector.transparent {
                border: none !important;
                opacity: 0 !important;
            }
            .approval-popover {
                min-width: 300px;
            }
            .approval-popover .approval-section {
                padding-top: 10px;
            }
            .approval-popover .approval-section .approval-section-label {
                font-weight: 700;
            }
            .tooltip-wrapper > omega-tooltip {
                width: 16px;
                display: inline-block;
            }
            .approver-name-list {
                display: grid;
                grid-auto-flow: column;
                grid-template-rows: repeat(4, auto);
            }
            .approver-name {
                min-width: 100px;
            }
            .pending-process-content-wrapper {
                display: flex;
                justify-content: center;
                align-items: center;
            }
            .pending-process-content {
                max-width: 250px;
                text-align: center;
                line-height: 1.35rem;
                text-transform: none;
            }
        `;
    }
}

customElements.define('channel-status-badge', ChannelStatusBadge);
